import React from 'react';
import { getTemplateHeaders } from './constants';
import { getNextFieldForNavigation } from './utils';
import FieldRenderer from './FieldRenderer';

const headerLabels = {
  ModeID: 'Mode ID',
  MatchType: 'Competition',
  MatchTypeId: 'Competition ID',
  MatchCategory: 'Match Category',
  MatchTypeRegion: 'Competition Region',
  WinLose: 'Result',
  TotalDamage: 'Total Damage',
  CaptureKills: 'Capture Kills',
  DistanceMoved: 'Distance Moved',
  OperatorValue: 'Operator Value',
  FirstBloods: 'First Bloods',
  Clutches1v1: 'Clutches 1v1',
  HillTime: 'Hill Time',
};

const hiddenFullTableFields = new Set(['Team', 'Region', 'VS', 'Date', 'ModeID']);

const getHeaderLabel = (fieldKey) => headerLabels[fieldKey] || fieldKey;

const getFieldClass = (fieldKey, activeMode) => {
  const base = String(fieldKey || '')
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .toLowerCase();
  const sticky =
    fieldKey === 'Player'
      ? ` full-table-cell--sticky-player ${activeMode === 'SND' ? 'is-sticky-first' : ''}`
      : fieldKey === 'Operator' && activeMode !== 'SND'
        ? ' full-table-cell--sticky-operator'
        : '';
  return `full-table-cell full-table-cell--${base}${sticky}`;
};

const StatsTable = ({
  activeMode,
  structuredData,
  updateCell,
  activePlayers,
  playerImages,
  matchTypes,
  vsTeams,
  inputRefs
}) => {
  if (!activeMode || !structuredData.length) return null;

  const headers = getTemplateHeaders(activeMode);
  const visibleHeaders = headers.filter(header => !hiddenFullTableFields.has(header));

  const handleKeyDown = (e, fieldKey, row) => {
    const isTab = e.key === 'Tab' && !e.shiftKey;
    const isEnter = e.key === 'Enter';

    if (!isTab && !isEnter) return;

    const currentIndex = structuredData.findIndex(r => r._rowId === row._rowId);
    const isLastRow = currentIndex === structuredData.length - 1;
    const refKey = (rid, col) => `${rid}-${col}`;

    // Advanced fields navigation for Hardpoint: TotalDamage → CaptureKills → DistanceMoved → next row TotalDamage...
    if (activeMode === 'HP' && isTab) {
      if (fieldKey === 'TotalDamage') {
        e.preventDefault();
        inputRefs.current[refKey(row._rowId, 'CaptureKills')]?.focus();
        return;
      }

      if (fieldKey === 'CaptureKills') {
        e.preventDefault();
        inputRefs.current[refKey(row._rowId, 'DistanceMoved')]?.focus();
        return;
      }

      if (fieldKey === 'DistanceMoved') {
        e.preventDefault();
        if (isLastRow) {
          const firstRow = structuredData[0];
          if (firstRow) {
            inputRefs.current[refKey(firstRow._rowId, 'OperatorValue')]?.focus();
          }
        } else {
          const nextRow = structuredData[currentIndex + 1];
          if (nextRow) {
            inputRefs.current[refKey(nextRow._rowId, 'TotalDamage')]?.focus();
          }
        }
        return;
      }
    }

    // Advanced fields navigation: ADR → FirstBloods → Clutches1v1 → next row ADR...
    if (isTab) {
      if (fieldKey === 'ADR') {
        e.preventDefault();
        inputRefs.current[refKey(row._rowId, 'FirstBloods')]?.focus();
        return;
      }

      if (fieldKey === 'FirstBloods') {
        e.preventDefault();
        inputRefs.current[refKey(row._rowId, 'Clutches1v1')]?.focus();
        return;
      }

      if (fieldKey === 'Clutches1v1') {
        e.preventDefault();
        if (isLastRow) {
          const firstRow = structuredData[0];
          if (firstRow) {
            inputRefs.current[refKey(firstRow._rowId, 'ADR')]?.focus();
          }
        } else {
          const nextRow = structuredData[currentIndex + 1];
          if (nextRow) {
            inputRefs.current[refKey(nextRow._rowId, 'ADR')]?.focus();
          }
        }
        return;
      }
    }

    const nextField = getNextFieldForNavigation(activeMode, fieldKey, isEnter, true);
    
    if (nextField) {
      const nextRow = structuredData[currentIndex + 1];
      if (nextRow) {
        e.preventDefault();
        inputRefs.current[refKey(nextRow._rowId, nextField)]?.focus();
      }
    }
  };

  return (
    <div className="parsed-table-block full-table-scroll">
      <table className={`editable-table full-table full-table--${String(activeMode || '').toLowerCase()}`}>
        <thead>
          <tr>
            {visibleHeaders.map((header, idx) => (
              <th key={idx} className={getFieldClass(header, activeMode)}>{getHeaderLabel(header)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {structuredData.map(row => (
            <tr key={row._rowId}>
              {visibleHeaders.map((fieldKey, idx) => {
                if (fieldKey === 'Operator' && activeMode === 'SND') {
                  return null;
                }

                return (
                  <td key={idx} className={getFieldClass(fieldKey, activeMode)}>
                    <FieldRenderer
                      fieldKey={fieldKey}
                      row={row}
                      value={row[fieldKey] || ''}
                      activeMode={activeMode}
                      activePlayers={activePlayers}
                      playerImages={playerImages}
                      matchTypes={matchTypes}
                      vsTeams={vsTeams}
                      structuredData={structuredData}
                      updateCell={updateCell}
                      inputRefs={inputRefs}
                      onKeyDown={(e) => handleKeyDown(e, fieldKey, row)}
                      showAllColumns={true}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default StatsTable;

